import React, { useState } from 'react';
import { getContentControlTags } from '../services/wordHelper';
import { getAuthHeaders, API_URL } from '../services/auth';

interface QualityIssue {
  severity: 'error' | 'warning' | 'info';
  category: string;
  message: string;
  location?: string;
  suggestion?: string;
}

interface QualityResult {
  score: number;
  passed: boolean;
  issues: QualityIssue[];
  summary: string;
}

export function ArtemisCheck({ templateId }: { templateId?: number }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<QualityResult | null>(null);
  const hasRun = React.useRef(false);

  // Auto-run on mount
  React.useEffect(() => {
    if (!hasRun.current) {
      hasRun.current = true;
      runCheck();
    }
  }, []);

  const runCheck = async () => {
    setRunning(true);
    setResult(null);
    try {
      // Extract text paragraph-by-paragraph so real line breaks survive.
      // body.text concatenates everything with no separator — headings merge
      // with the following line (e.g. "...BOND" + "BOND NO." → "BONDBOND
      // NO.") which Claude then flags as a duplicate word.
      let documentText = '';
      try {
        await Word.run(async (context) => {
          const paragraphs = context.document.body.paragraphs;
          paragraphs.load('text');
          await context.sync();
          documentText = paragraphs.items.map(p => p.text).join('\n');
        });
      } catch { /* ignore */ }

      if (!documentText || documentText.trim().length < 10) {
        setResult({
          score: 0, passed: false,
          issues: [{ severity: 'error', category: 'Error', message: 'Document is empty or too short to check.' }],
          summary: 'No content to validate',
        });
        return;
      }

      // Get placeholder tags
      let placeholderTags: string[] = [];
      try {
        const tags = await getContentControlTags();
        placeholderTags = [...new Set(tags.map(t => t.tag))];
      } catch { /* ignore */ }

      const resp = await fetch(`${API_URL}/ai/quality-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ templateId, documentText, placeholderTags }),
      });
      const data = await resp.json();
      if (data.success) {
        setResult(data.data);
      } else {
        throw new Error(data.error?.message || 'Check failed');
      }
    } catch (err: any) {
      setResult({
        score: 0, passed: false,
        issues: [{ severity: 'error', category: 'Error', message: err?.message || 'Quality check failed.' }],
        summary: 'Check failed',
      });
    } finally {
      setRunning(false);
    }
  };

  const severityIcon = (s: string) => s === 'error' ? '❌' : s === 'warning' ? '⚠️' : 'ℹ️';
  const severityColor = (s: string) => ({
    bg: s === 'error' ? '#fef2f2' : s === 'warning' ? '#fffbeb' : '#f0f9ff',
    border: s === 'error' ? '#fecaca' : s === 'warning' ? '#fde68a' : '#bae6fd',
    text: s === 'error' ? '#991b1b' : s === 'warning' ? '#92400e' : '#075985',
  });

  return (
    <div style={{ padding: 16, fontFamily: "'Segoe UI', sans-serif" }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 28, marginBottom: 4 }}>✨</div>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Artemis Check</h2>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>
          AI-powered template validation
        </p>
      </div>

      {/* Loading state */}
      {running && (
        <div style={{ textAlign: 'center', padding: '30px 0' }}>
          <div style={{
            width: 32, height: 32, margin: '0 auto 12px',
            border: '3px solid #e2e8f0', borderTopColor: '#0d9488',
            borderRadius: '50%', animation: 'spin 0.8s linear infinite',
          }} />
          <p style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>Analyzing template...</p>
          <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Checking spelling, grammar, and placeholders</p>
        </div>
      )}

      {/* Results */}
      {result && (
        <div>
          {/* Score */}
          <div style={{
            background: result.passed ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${result.passed ? '#bbf7d0' : '#fecaca'}`,
            borderRadius: 10,
            padding: 16,
            textAlign: 'center',
            marginBottom: 16,
          }}>
            <div style={{ fontSize: 36, fontWeight: 800, color: result.score >= 80 ? '#16a34a' : result.score >= 60 ? '#d97706' : '#dc2626' }}>
              {result.score}
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>
              Quality Score
            </div>
            {/* Score bar */}
            <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden', marginTop: 10 }}>
              <div style={{
                height: '100%',
                width: `${result.score}%`,
                background: result.score >= 80 ? '#16a34a' : result.score >= 60 ? '#d97706' : '#dc2626',
                borderRadius: 3,
                transition: 'width 0.5s ease',
              }} />
            </div>
            <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: result.passed ? '#166534' : '#991b1b' }}>
              {result.passed ? '✅ PASSED' : '⚠️ NEEDS REVIEW'}
            </div>
          </div>

          {/* Summary */}
          <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>{result.summary}</p>

          {/* Issues */}
          {result.issues.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {result.issues.map((issue, i) => {
                const colors = severityColor(issue.severity);
                return (
                  <div key={i} style={{
                    padding: '10px 12px',
                    borderRadius: 8,
                    background: colors.bg,
                    border: `1px solid ${colors.border}`,
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 12, color: colors.text, marginBottom: 2 }}>
                      {severityIcon(issue.severity)} {issue.category}
                    </div>
                    <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.4 }}>
                      {issue.message}
                    </div>
                    {issue.location && (
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>
                        📍 "{issue.location}"
                      </div>
                    )}
                    {issue.suggestion && (
                      <div style={{ fontSize: 11, color: '#0d9488', marginTop: 3, fontStyle: 'italic' }}>
                        💡 {issue.suggestion}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {result.issues.length === 0 && (
            <div style={{ textAlign: 'center', padding: 20, color: '#166534', fontSize: 14 }}>
              ✅ Template looks great — no issues found!
            </div>
          )}

          {/* Re-run button */}
          <button
            onClick={runCheck}
            style={{
              width: '100%',
              padding: '8px',
              marginTop: 16,
              background: 'transparent',
              border: '1px solid #e2e8f0',
              borderRadius: 6,
              fontSize: 12,
              color: '#64748b',
              cursor: 'pointer',
            }}
          >
            🔄 Run Again
          </button>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
